/*! Copyright (c) Safe As Milk. All rights reserved. */
import Cart from "cart-store";

import { formatMoney } from "utils";

class CartRecommendations extends HTMLElement {
    async connectedCallback() {
        this.sectionId = this.getAttribute("section-id");
        this.recommendationsAmount = 4;
        this.recommendations = [];
        this.sectionTitle = this.querySelector("cart-recommendations-title");
        this.container = this.querySelector("[cart-recommendations-content]");
        this.itemsScroll = this.querySelector("items-scroll");
        this.intent = this.getAttribute("product-recommendations-type");
        this.freeShippingThreshold = Number(this.getAttribute("free-shipping-threshold")) || 0;
        this.freeShippingPaidRate = Number(this.getAttribute("free-shipping-paid-rate")) || 0;
        this.freeShippingActivationRatio = Number(this.getAttribute("free-shipping-activation-ratio")) || .4;
        this.freeShippingGapTemplate = this.getAttribute("free-shipping-gap-template") || "";
        this.freeShippingGapElement = this.querySelector("[data-free-shipping-gap]");
        this.cards = Array.from(this.container.children);
        this.benefitCache = new Map;
        this.unsubscribe = Cart.subscribe(((state, prevState) => {
            const getCartProductsWithoutBeingAdded = (items, productVariantsBeingAdded) => items.reduce(((ids, item) => {
                const id = item.product_id.toString();
                if (productVariantsBeingAdded.includes(item.variant_id.toString())) return ids;
                if (!ids.includes(id)) {
                    ids.push(id);
                }
                return ids;
            }), []);
            const previousFinalCartProductsIds = getCartProductsWithoutBeingAdded(prevState.cart.items, prevState.productVariantsBeingAdded);
            const finalCartProductsIds = getCartProductsWithoutBeingAdded(state.cart.items, state.productVariantsBeingAdded);
            const prepareRecommendations = (recommendationItems, cartProductsIds) => Object.values(recommendationItems).reduce(((recommendations, value) => {
                const weightedRecommendations = value.products.reduce(((uniqueProducts, product, i) => {
                    if (!recommendations.find((r => r.item.id === product.id)) && !cartProductsIds.includes(product.id.toString())) {
                        uniqueProducts.push({
                            weight: (i + 1) / value.weight + (product._venueCartFallback ? 1000 : 0),
                            item: product
                        });
                    }
                    return uniqueProducts;
                }), []);
                return [ ...recommendations, ...weightedRecommendations ];
            }), []).sort(((a, b) => a.weight - b.weight)).slice(0, this.recommendationsAmount);
            const rankForFreeShipping = (recommendations, cartTotal, updateMessage = false) => {
                const threshold = this.freeShippingThreshold;
                if (!threshold || cartTotal <= 0 || cartTotal >= threshold || recommendations.length === 0) {
                    if (updateMessage) this.#updateFreeShippingGap();
                    return recommendations;
                }
                const gap = threshold - cartTotal;
                if (gap > threshold * this.freeShippingActivationRatio) {
                    if (updateMessage) this.#updateFreeShippingGap();
                    return recommendations;
                }
                if (updateMessage) this.#updateFreeShippingGap(gap);
                return recommendations.map(((recommendation, index) => {
                    const price = Number(recommendation.item.price) || 0;
                    const distance = price > 0 ? Math.abs(price - gap) : Number.MAX_SAFE_INTEGER;
                    const relevancePenalty = (Number(recommendation.weight) || 0) * 100;
                    const reasonableOvershoot = this.freeShippingPaidRate;
                    let shippingFitBucket = 2;
                    if (price >= gap && reasonableOvershoot > 0 && price - gap <= reasonableOvershoot) {
                        shippingFitBucket = 0;
                    } else if (price > 0 && price < gap) {
                        shippingFitBucket = 1;
                    }
                    return {
                        ...recommendation,
                        freeShippingScore: shippingFitBucket * threshold * 2 + distance + relevancePenalty,
                        originalIndex: index
                    };
                })).sort(((a, b) => a.freeShippingScore === b.freeShippingScore ? a.originalIndex - b.originalIndex : a.freeShippingScore - b.freeShippingScore));
            };
            const newRecommendations = rankForFreeShipping(prepareRecommendations(state.recommendations.items, finalCartProductsIds), state.cart.total_price, true);
            const oldRecommendations = rankForFreeShipping(prepareRecommendations(prevState.recommendations.items, previousFinalCartProductsIds), prevState.cart.total_price);
            if (newRecommendations.map((({item: item}) => item.id)).toString() === oldRecommendations.map((({item: item}) => item.id)).toString()) return;
            this.recommendations = newRecommendations;
            this.cards = this.cards.reduce(((newItems, item) => {
                const cardProductId = item.getAttribute("product-id");
                const itemInRecommendations = this.recommendations.find((({item: i}) => i.id.toString() === cardProductId));
                if (!itemInRecommendations) {
                    item.remove();
                    return newItems;
                }
                newItems.push(item);
                return newItems;
            }), []);
            const currentCardsProductIds = this.cards.map((card => card.getAttribute("product-id")));
            const newCards = [];
            this.recommendations.forEach((({item: item}, i) => {
                if (!currentCardsProductIds.includes(item.id.toString())) {
                    const newCard = this.#createCard();
                    if (i === 0) {
                        this.container.prepend(newCard);
                    } else {
                        newCards[i - 1].after(newCard);
                    }
                    newCard.sectionId = this.sectionId;
                    newCard.product = item;
                    newCard.id = `cart-recommendation--${item.id}`;
                    newCards.push(newCard);
                } else {
                    const oldCard = this.cards.find((j => j.getAttribute("product-id") === item.id.toString()));
                    if (currentCardsProductIds.indexOf(oldCard.getAttribute("product-id")) !== i) {
                        if (i === 0) {
                            this.container.prepend(oldCard);
                        } else {
                            newCards[i - 1].after(oldCard);
                        }
                    }
                    newCards.push(oldCard);
                }
            }));
            this.cards = newCards;
            this.cards.forEach(((card, index) => {
                const item = this.recommendations[index]?.item;
                if (item) this.#setCardBenefit(card, item);
            }));
            if (this.itemsScroll) this.itemsScroll.smoothScrollItems(0);
            if (this.recommendations.length > 0) {
                this.removeAttribute("hidden");
            } else {
                this.setAttribute("hidden", "");
            }
        }));
        await Cart.enableCartRecommendations(this.intent);
    }
    disconnectedCallback() {
        this.unsubscribe();
    }
    #updateFreeShippingGap(amount = 0) {
        if (!this.freeShippingGapElement) return;
        if (!amount || !this.freeShippingGapTemplate) {
            this.freeShippingGapElement.setAttribute("hidden", "");
            this.freeShippingGapElement.textContent = "";
            return;
        }
        const formattedAmount = formatMoney(amount, window.theme.money_format).replace(/[.,]00$/, "");
        this.freeShippingGapElement.textContent = this.freeShippingGapTemplate.replace("__AMOUNT__", formattedAmount);
        this.freeShippingGapElement.removeAttribute("hidden");
    }
    async #setCardBenefit(card, item) {
        if (!card || !item?.id || !item?.handle) return;
        const details = card.querySelector(".product-card__details");
        if (!details) return;
        let benefit = this.benefitCache.get(item.id);
        if (benefit === undefined) {
            benefit = "";
            try {
                const rootRoute = window.Shopify?.routes?.root ?? "/";
                const response = await fetch(`${rootRoute}products/${item.handle}?section_id=cart-addon-benefit`);
                if (response.ok) {
                    const wrapper = document.createElement("div");
                    wrapper.innerHTML = await response.text();
                    benefit = wrapper.querySelector("[data-cart-addon-benefit]")?.textContent?.trim() || "";
                }
            } catch (error) {}
            this.benefitCache.set(item.id, benefit);
        }
        if (!benefit || !card.isConnected || card.getAttribute("product-id") !== item.id.toString()) return;
        let benefitElement = details.querySelector(".cart-recommendation__benefit");
        if (!benefitElement) {
            benefitElement = document.createElement("p");
            benefitElement.className = "cart-recommendation__benefit";
            const button = details.querySelector("product-card-button");
            if (button) {
                button.before(benefitElement);
            } else {
                details.appendChild(benefitElement);
            }
        }
        benefitElement.textContent = benefit;
        benefitElement.setAttribute("title", benefit);
        const button = details.querySelector("product-card-button");
        const price = details.querySelector(".product-card__price");
        if (button && price) button.before(price);
    }
    #createCard() {
        const cardTemplate = document.getElementById(`${this.sectionId}--template-cart-recommendations-card`).content;
        const cardFragment = cardTemplate.cloneNode(true);
        const card = cardFragment.children[0];
        return card;
    }
}

customElements.define("cart-recommendations", CartRecommendations);
//# sourceMappingURL=cart-recommendations.js.map
